import { fromTradingViewResolution } from '@talos/kyoko';
import { initialOptionFilterPanelState } from '../../containers/Trading/Markets/Options/Filter/filterSlice';
import type { TypedGroupviewPanelConfigState } from '../../types/LayoutConfig';
import type { GeneralMarketTab } from '../MarketTabs.types';
import { type MarketTab, MarketTabType } from '../MarketTabs.types';
import { fillToNumColumns } from './fillToNumColumns';

export const mapTabToPanel = (tab: MarketTab): TypedGroupviewPanelConfigState => {
  const shared = {
    id: tab.id,
    title: tab.label,
  };
  const defaultParams = { editable: true, closable: true };
  switch (tab.type) {
    case MarketTabType.Option:
      return {
        ...shared,
        contentComponent: 'options',
        params: {
          ...defaultParams,
          type: 'options',
          optionFilter: initialOptionFilterPanelState.optionFilter,
          strikeFilter: initialOptionFilterPanelState.strikeFilter,
          ...tab.data,
        },
      };
    case MarketTabType.OrderDetails:
      return {
        ...shared,
        contentComponent: 'orderDetails',
        params: {
          ...defaultParams,
          type: 'orderDetails',
          orderID: tab.orderID,
          openTab: tab.openTab,
          orderType: 'order',
        },
      };
    case MarketTabType.DeepDive:
      return {
        ...shared,
        contentComponent: 'deepDive',
        params: {
          ...defaultParams,
          type: 'deepDive',
          symbol: tab.symbol,
          markets: tab.data.selectedMarkets,
          marketAccounts: tab.data.selectedMarketAccounts,
          chartResolution:
            tab.data.chartResolution != null ? fromTradingViewResolution(tab.data.chartResolution) : '1H',
          annotations: [],
          chartType: 'candlestick',
        },
      };
    case MarketTabType.ReconDetails:
      return {
        ...shared,
        contentComponent: 'reconMismatchesDetails',
        params: {
          ...defaultParams,
          type: 'reconMismatchesDetails',
          mismatchIDs: tab.mismatchIDs,
        },
      };
    case MarketTabType.Multileg:
      return {
        ...shared,
        contentComponent: 'multilegDeepDive',
        params: {
          ...defaultParams,
          type: 'multilegDeepDive',
          resolvedSymbol: tab.data.resolvedSymbol,
          instrumentType: tab.data.instrumentType,
        },
      };
    case MarketTabType.DealProductBuilder:
      return {
        ...shared,
        contentComponent: 'dealProductBuilder',
        params: {
          ...defaultParams,
          type: 'dealProductBuilder',
          bookingType: tab.data.bookingType,
          resolvedSymbol: tab.data.resolvedSymbol,
        },
      };
    case MarketTabType.AccountLedgerEventsDetails:
      return {
        ...shared,
        contentComponent: 'accountLedgerEvents',
        params: {
          ...defaultParams,
          type: 'accountLedgerEvents',
          account: tab.account,
          accountType: tab.accountType,
          asset: tab.asset,
        },
      };
    case MarketTabType.PositionAutoHedging:
      return {
        ...shared,
        contentComponent: 'positionAutoHedgingRules',
        params: {
          ...defaultParams,
          type: 'positionAutoHedgingRules',
        },
      };
    case MarketTabType.CareOrders:
      return {
        ...shared,
        contentComponent: 'careOrders',
        params: {
          ...defaultParams,
          type: 'careOrders',
          tabs: undefined,
        },
      };
    case MarketTabType.Market:
      return {
        ...shared,
        contentComponent: 'marketCards',
        params: {
          ...defaultParams,
          type: 'marketCards',
          sortCards: false,
          columns: fillToNumColumns(tab.columns ?? []),
          defaults: tab.defaults,
        },
      };
    case MarketTabType.PredictionMarket:
      return {
        ...shared,
        contentComponent: 'predictionMarkets',
        params: {
          ...defaultParams,
          type: 'predictionMarkets',
          sortCards: false,
          columns: fillToNumColumns(tab.columns ?? []),
        },
      };
    case MarketTabType.ManualRFQs:
      return {
        ...shared,
        contentComponent: 'manualRfqs',
        params: {
          ...defaultParams,
          type: 'manualRfqs',
          showAllDecimals: false,
        },
      };
    // Treat tab without a type as a Market tab
    default: {
      const legacyTab: never = tab;

      return {
        ...shared,
        contentComponent: 'marketCards',
        params: {
          ...defaultParams,
          type: 'marketCards',
          sortCards: false,
          columns: fillToNumColumns((legacyTab as GeneralMarketTab).columns ?? []),
          defaults: (legacyTab as GeneralMarketTab).defaults,
        },
      };
    }
  }
};
